import { useEffect, useRef, useState } from 'react'
import { Navigate, NavLink, useSearchParams } from 'react-router'
import { useDispatch } from 'react-redux'
import { AssignRoleResponse, SignUpLinkResponse } from '../../generated'
import { useSignInWithLink } from '../../app/hooks/UseSignInWithLink.ts'
import { setNeedsRegistration } from '../../features/FirebaseAuthSlice.ts'
import GenericErrorPage from '../../pages/GenericErrorPage.tsx'
import SignInLoadingSpinner from './SignInLoadingSpinner.tsx'
import {
  useCreateGuestUserMutation,
  useScheduleRoleMutation,
} from '../../features/SpeechAgendaApiSlice.ts'
import { setWelcomeMessage } from '../../features/SignUpWelcomeMessageSlice.ts'
import { formatAssignRoleResponse } from '../../utils/FormatterUtils.ts'
import { API_BASE_URL } from '../../features/SpeechAgendaApiSlice.ts'

export default function SignUpWithLink() {
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const code = searchParams.get('code')
  const effectRan = useRef(false) // Track execution

  const [hasSignUpDataError, setHasSignUpDataError] = useState(false)
  const [signUpComplete, setSignUpComplete] = useState(false)

  const [loginState, signInWithLink] = useSignInWithLink()
  const [createGuestUser] = useCreateGuestUserMutation()
  const [scheduleRole] = useScheduleRoleMutation()

  const [clubId, setClubId] = useState<string>()
  const [meetingTimestamp, setMeetingTimestamp] = useState<string>()

  useEffect(() => {
    if (!code || effectRan.current) return
    effectRan.current = true // Mark as executed
    fetchSignUpDataByCode(code)
    // fetchSignUpDataByCode is a stable run-once handler guarded by effectRan; intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  // NOTE: RTK Query (lazy query) was initially attempted here but consistently faced initialization issues.
  // Due to persistent state and hook update problems, it was refactored to use a simple fetch call, which resolved all issues.

  //1. Get User data by code
  //2. Sign in with firebase
  //3. Create user on the back-end using token
  //4. Schedule a role
  const fetchSignUpDataByCode = async (code: string) => {
    let response
    try {
      //1. Get User data by code
      response = await fetch(`${API_BASE_URL}/auth/sign-up/${code}`)
    } catch {
      setHasSignUpDataError(true)
    }

    if (!response || !response.ok) {
      setHasSignUpDataError(true)
      return
    }

    const data: SignUpLinkResponse | null = await response.json()
    if (!data) {
      setHasSignUpDataError(true)
      return
    }
    if (!data.clubId) {
      setHasSignUpDataError(true)
      return
    }
    if (!data.meetingTimestamp) {
      setHasSignUpDataError(true)
      return
    }
    if (!data.name) {
      setHasSignUpDataError(true)
      return
    }
    if (!data.email) {
      setHasSignUpDataError(true)
      return
    }

    setClubId(data.clubId.toString())
    setMeetingTimestamp(data.meetingTimestamp.toString())

    //Prevent from requesting user after logging in with firebase
    dispatch(setNeedsRegistration(true))

    //2. Sign in with firebase
    const userCredentials = await signInWithLink(data.email)
    const token = await userCredentials?.user.getIdToken(true)
    if (!token) {
      setHasSignUpDataError(true)
      return
    }

    //3. Create user on the back-end using token
    try {
      await createGuestUser({
        accessToken: token,
        body: { name: data.name, email: data.email, clubId: data.clubId },
      }).unwrap()
    } catch {
      setHasSignUpDataError(true)
      return
    }
    dispatch(setNeedsRegistration(false))

    const ROLE_ID_GUEST: number = 11

    //4. Schedule a role
    let scheduledRoleResponse: AssignRoleResponse
    try {
      scheduledRoleResponse = await scheduleRole({
        clubId: data.clubId.toString(),
        meetingTimestampUtc: data.meetingTimestamp.toString(),
        body: { roleId: ROLE_ID_GUEST },
      }).unwrap()
    } catch {
      setHasSignUpDataError(true)
      return
    }
    const { title, description } = formatAssignRoleResponse(
      scheduledRoleResponse
    )
    dispatch(
      setWelcomeMessage({
        present: true,
        title,
        description,
      })
    )
    setSignUpComplete(true)
  }

  if (loginState === 'FAILED' || hasSignUpDataError) {
    return (
      <GenericErrorPage
        title="Sign-Up Failed"
        description="An error occurred during sign-up, or the link you used is invalid, expired, or already used. Each sign-up link can only be used once. Please request a new link or try signing up in another way">
        <NavLink
          to="/login"
          className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          Back to Login
        </NavLink>
      </GenericErrorPage>
    )
  }

  if (!signUpComplete) {
    return <SignInLoadingSpinner />
  }

  return <Navigate replace to={`/club/${clubId}/meeting/${meetingTimestamp}`} />
}
